import React, { useState } from "react";
import { useSelector } from "react-redux";
import ErrorText from "../../components/errorText";
import TriggerSelectionPopup from "./TriggerSelectionPopup";
import { 
  User, 
  FileText, 
  Calendar,
  MessageCircle, 
  Mail, 
  Receipt,
  ScrollText,
  Eye,
  Tag,
  ClipboardList,
  ChevronDown,
  ListChecks,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  PhoneCall,
  Edit
} from 'lucide-react';

// CSS Modules import
import styles from './triggerSection.module.css';
import './TriggerSelectionPopup.css'; // Keep this for popup styles
import logger from "../../utils/logger";

const TIME_BASED_EVENTS = ['task_due_date', 'task_completed'];

const TriggerSection = ({
  automationData,
  automationWhenTriggerData,
  isShowAutoEvent,
  isUpdateAuto,
  handleAutoFormChange,
  sourceByData,
  statusLabelData,
  viewPageData,
  viewFileData,
  taskData,
  activityListData,
  customActivityType,
  organizationLeadList,
  errors,
}: any) => {
  const [showTriggerPopup, setShowTriggerPopup] = useState(false);
  
  const StoreData = useSelector((state: any) => {
    return state?.rootReducers;
  });
  const { customSource } = StoreData?.user?.userPreferences;

  // Debug logging
  logger.log("TriggerSection props:", { 
    automationData, 
    isShowAutoEvent, 
    statusLabelData 
  });

  // Get display name for the selected trigger
  const getTriggerDisplayName = () => {
    if (!automationWhenTriggerData || !Array.isArray(automationWhenTriggerData)) {
      return "Select trigger";
    }
    const selected = automationWhenTriggerData.find(
      (item: any) => item.value === automationData?.event
    );
    return selected ? selected.name : "Select trigger";
  };

  // Get source label based on event type
  const getSourceLabel = (event: string): string => {
    switch (event) {
      case "create_lead":
        return "Lead source is";
      case "status_change":
        return "Status is updated to";
      case "label_change":
        return "Label is updated to";
      case "view_page":
        return "Page is";
      case "view_file":
        return "File is";
      case "create_activity":
        return "Activity is";
      case "create_task":
        return "Task type is";
      case "new_lead_added":
        return "List name";
      case "task_due_date":
      case "task_completed":
        return "Task type is";
      default:
        return "";
    }
  };

  // Handle trigger selection
  const handleTriggerSelect = (value: string) => {
    // Create a proper synthetic event to fix destructuring error
    const syntheticEvent = {
      target: { name: 'event', value },
      currentTarget: { name: 'event', value }
    };
    
    logger.log('Trigger selected:', value);
    handleAutoFormChange(syntheticEvent);
    setShowTriggerPopup(false);
  };

  // Format trigger options for popup
  const formatTriggerOptions = () => {
    logger.log('🔍 DEBUG: automationWhenTriggerData:', automationWhenTriggerData);
    
    if (!automationWhenTriggerData || !Array.isArray(automationWhenTriggerData)) {
      logger.log('❌ No trigger data available');
      return [];
    }
    
    const formatted = automationWhenTriggerData.map((item: any) => ({
      value: item.value,
      name: item.name || getTriggerNameByValue(item.value),
      description: item.description || getTriggerDescriptionByValue(item.value),
      category: item.category || getTriggerCategoryByValue(item.value),
      icon: getIconForTrigger(item.value)
    }));
    
    logger.log('✅ Formatted trigger options:', formatted);
    logger.log('🕐 Time-based triggers found:', formatted.filter(t => ['task_due_date', 'task_completed'].includes(t.value)));
    
    return formatted;
  };

  return (
    <div className={`col-md-12 when_then trigger-section ${styles.triggerSectionContainer}`}>
      {/* Step indicator */}
      <span className={styles.autoDot}>
        <label className={styles.dotNo}>1</label>
      </span>
      
      {/* Section header */}
      <label className={styles.whenThenLabel}>When this happens...</label>
      <span className={styles.whenThenTrigger}>
        Choose the event that starts your automation
      </span>
      
      <div className={styles.mainColumn}>
        {/* Visual trigger selector button */}
        <div className={styles.autoSelect}>
          <div 
            className={`${styles.triggerSelectorButton} ${isUpdateAuto ? styles.disabled : ""}`}
            onClick={() => {
              logger.log('Trigger selector clicked, isUpdateAuto:', isUpdateAuto);
              if (!isUpdateAuto) {
                setShowTriggerPopup(true);
              }
            }}
          >
            <span className={styles.triggerSelectedText}>
              {getTriggerDisplayName()}
            </span>
            {!isUpdateAuto && (
              <span className={styles.triggerSelectorArrow}>
                <ChevronDown size={16} />
              </span>
            )}
          </div>
          
          {/* Hidden original select for form compatibility */}
          <select
            name="event"
            value={automationData?.event}
            className={styles.hiddenSelect}
            onChange={(e) => handleAutoFormChange(e)}
            style={{ display: 'none' }}
          >
            <option value="">Select trigger</option>
            {automationWhenTriggerData && Array.isArray(automationWhenTriggerData) ? (
              automationWhenTriggerData.map((data: any, i: number) => (
                <option value={data.value} key={i}>
                  {data.name}
                </option>
              ))
            ) : null}
          </select>
        </div>

        {/* Trigger selection popup */}
        <TriggerSelectionPopup
          isOpen={showTriggerPopup}
          onClose={() => {
            logger.log('Trigger popup closed');
            setShowTriggerPopup(false);
          }}
          onSelect={handleTriggerSelect}
          currentValue={automationData?.event}
          isEditMode={isUpdateAuto}
          triggerOptions={formatTriggerOptions()}
        />

        {/* Condition dropdown - only show for certain events */}
        {(isShowAutoEvent && !TIME_BASED_EVENTS.includes(automationData?.event)) && (
          <div className={styles.conditionGroup}>
            <div className={styles.conditionRow}>
              <span className={styles.andLabel}>AND</span>
              <span className={styles.andSource}>
                {getSourceLabel(automationData?.event)}
              </span>
              <select
                name="targetValues"
                className={`${styles.formSelect} ${isUpdateAuto ? styles.disabled : ""}`}
                disabled={isUpdateAuto}
                value={automationData?.targetValues}
                onChange={(e) => {
                  logger.log('Target value changed:', e.target.value);
                  handleAutoFormChange(e);
                }}
              >
                <option value="">Select</option>
                
             {/* Create Lead options */}
{automationData?.event === "create_lead" && (
  <>
    <option value="">Select Lead Source</option>
    
    {(() => {
      logger.log('🔍 DEBUG SOURCE SELECTION:', {
        isUpdateAuto: isUpdateAuto,
        targetValues: automationData?.targetValues,
        isArray: Array.isArray(automationData?.targetValues),
        length: Array.isArray(automationData?.targetValues) ? automationData.targetValues.length : 'not array',
        type: typeof automationData?.targetValues
      });

      // EDIT mode: Show "Source + X others" for multiple selections
      if (isUpdateAuto && Array.isArray(automationData?.targetValues) && automationData.targetValues.length > 1) {
        logger.log('✅ SHOWING SMART DISPLAY');
        const firstName = sourceByData?.find((s: any) => s._id === automationData.targetValues[0])?.name || 
                         customSource?.find((s: any) => s.value === automationData.targetValues[0])?.name || 
                         'Source';
        const othersCount = automationData.targetValues.length - 1;
        
        return (
          <option value={automationData.targetValues} selected>
            {firstName} + {othersCount} others
          </option>
        );
      }

      logger.log('❌ NOT SHOWING SMART DISPLAY - using normal dropdown');

      // Normal mode: Show all sources
      return (
        <>
          {customSource && sourceByData ? (
            [
              ...(customSource?.map((e: any) => ({
                ...e,
                _id: e.value,
                isActive: true,
              })) || []),
              ...(sourceByData || []),
            ]
              .filter((item) => item && item.isActive)
              .map((item: any, i: number) => (
                <option value={item._id} key={i}>
                  {item.name}
                </option>
              ))
          ) : (
            <option value="" disabled>No lead source options available</option>
          )}
          <option value="any">Any</option>
        </>
      );
    })()}
  </>
)}

                {/* Status/Label Change options */}
                {(automationData?.event === "status_change" ||
                  automationData?.event === "label_change") && (
                  <>
                    {statusLabelData && statusLabelData.length > 0 ? (
                      statusLabelData.map((item: any, i: number) => (
                        <option value={item.value} key={i}>
                          {item.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No status/label options available</option>
                    )}
                    <option value="any">Any</option>
                  </>
                )}

                {/* View Page options */}
                {automationData?.event === "view_page" && (
                  <>
                    {viewPageData && viewPageData.length > 0 ? (
                      viewPageData.map((item: any, i: number) => (
                        <option value={item._id} key={i}>
                          {item.details.title}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No page options available</option>
                    )}
                    <option value="any">Any</option>
                  </>
                )}

                {/* View File options */}
                {automationData?.event === "view_file" && (
                  <>
                    {viewFileData && viewFileData.length > 0 ? (
                      viewFileData.map((item: any, i: number) => (
                        <option value={item._id} key={i}>
                          {item?.details?.title}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No file options available</option>
                    )}
                    <option value="any">Any</option>
                  </>
                )}

                {/* Create Task options */}
                {automationData?.event === "create_task" && (
                  <>
                    {taskData && taskData.length > 0 ? (
                      taskData.map((item: any, i: number) => (
                        <option value={item.value} key={i}>
                          {item.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No task options available</option>
                    )}
                    <option value="any">Any</option>
                  </>
                )}

                {/* Edit Task options */}
                {automationData?.event === "edit_task" && (
                  <>
                    <option value="toBePerformAt">Due date is updated</option>
                    <option value="isCompleted">Task is completed</option>
                    <option value="any">Any</option>
                  </>
                )}

                {/* Create Activity options */}
                {automationData?.event === "create_activity" && (
                  <>
                    {activityListData && customActivityType ? (
                      [...(activityListData || []), ...(customActivityType || [])].map(
                        (item: any, i: number) => (
                          <option value={item.value} key={i}>
                            {item.name}
                          </option>
                        )
                      )
                    ) : (
                      <option value="" disabled>No activity options available</option>
                    )}
                    <option value="any">Any</option>
                  </>
                )}

                {/* New Lead Added options */}
                {automationData?.event === "new_lead_added" && (
                  <>
                    {organizationLeadList && organizationLeadList.length > 0 ? (
                      organizationLeadList.map((item: any, i: number) => (
                        <option value={item._id} key={i}>
                          {item.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No lead list options available</option>
                    )}
                  </>
                )}

                {/* Time-based Task options */}
                {(automationData?.event === "task_due_date" || automationData?.event === "task_completed") && (
                  <>
                    {taskData && taskData.length > 0 ? (
                      taskData.map((item: any, i: number) => (
                        <option value={item.value} key={i}>
                          {item.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No task type options available</option>
                    )}
                    <option value="any">Any Task Type</option>
                  </>
                )}
              </select>
            </div>
          </div>
        )}
        
        {/* Error display */}
        {errors.event && (
          <div className={styles.errorText}>
            <ErrorText message={errors.event} />
          </div>
        )}
      </div>
    </div>
  );
};

// Helper functions to provide standardized names, descriptions, and categories
const getTriggerNameByValue = (value: string): string => {
  switch (value) {
    case 'create_lead': return 'New Lead Added';
    case 'edit_lead': return 'Lead Information Updated';
    case 'status_change': return 'Lead Status Changed';
    case 'label_change': return 'Lead Label Changed';
    case 'new_lead_added': return 'Lead Added to List';
    case 'create_quotation': return 'New Quotation Created';
    case 'edit_quotation': return 'Quotation Updated';
    case 'create_invoice': return 'New Invoice Created';
    case 'edit_invoice': return 'Invoice Updated';
    case 'create_order': return 'New Order Created';
    case 'edit_order': return 'Order Updated';
    case 'create_task': return 'New Task Created';
    case 'edit_task': return 'Task Updated/Completed';
    case 'create_activity': return 'New Activity Logged';
    case 'view_page': return 'Lead Views Page';
    case 'view_file': return 'Lead Views Document';
    case 'send_email': return 'Email Sent';
    case 'send_whatsapp': return 'WhatsApp Message Sent';
    case 'task_due_date': return 'Task Due Date';
    case 'task_completed': return 'Task Completed';
    default: return value.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  }
};

const getTriggerDescriptionByValue = (value: string): string => {
  switch (value) {
    case 'create_lead': return 'Triggers when a new lead is created in your CRM';
    case 'edit_lead': return 'Triggers when any lead information is changed';
    case 'status_change': return 'Triggers when a lead moves to a different status';
    case 'label_change': return 'Triggers when a lead\'s label is updated';
    case 'new_lead_added': return 'Triggers when a lead is added to a specific list';
    case 'create_quotation': return 'Triggers when you create a new quotation for a lead';
    case 'edit_quotation': return 'Triggers when a quotation is edited or status changes';
    case 'create_invoice': return 'Triggers when you create a new invoice for a lead';
    case 'edit_invoice': return 'Triggers when an invoice is edited or status changes';
    case 'create_order': return 'Triggers when you create a new order for a lead';
    case 'edit_order': return 'Triggers when an order is edited or status changes';
    case 'create_task': return 'Triggers when you create a new follow-up task';
    case 'edit_task': return 'Triggers when a task is updated or marked as complete';
    case 'create_activity': return 'Triggers when you log a new activity with a lead';
    case 'view_page': return 'Triggers when a lead views a specific page on your website';
    case 'view_file': return 'Triggers when a lead opens a file you\'ve shared';
    case 'send_email': return 'Triggers when an email is sent to a lead';
    case 'send_whatsapp': return 'Triggers when a WhatsApp message is sent to a lead';
    case 'task_due_date': return 'Triggers before or after a task\'s due date';
    case 'task_completed': return 'Triggers before or after a task is completed';
    default: return 'Triggers when this event occurs';
  }
};

const getTriggerCategoryByValue = (value: string): string => {
  // Check time-based triggers first
  if (value === 'task_due_date' || value === 'task_completed') {
    return 'Time-Based Triggers';
  }
  else if (value.includes('lead') || value === 'status_change' || value === 'label_change' || value === 'new_lead_added') {
    return 'Lead Management';
  } else if (value.includes('quotation') || value.includes('invoice') || value.includes('order')) {
    return 'Sales & Quotes';
  } else if (value.includes('task') || value.includes('activity') || value.includes('view_') || value.includes('call')) {
    return 'Activities & Tasks';
  } else if (value.includes('email') || value.includes('whatsapp')) {
    return 'Communication';
  } else {
    return 'Other';
  }
};

// Helper function to get icon for trigger type
const getIconForTrigger = (triggerValue: string) => {
  switch (triggerValue) {
    case 'create_lead':
    case 'edit_lead':
    case 'new_lead_added':
      return <User size={22} />;
    case 'status_change':
      return <ListChecks size={22} />;
    case 'label_change':
      return <Tag size={22} />;
    case 'create_quotation':
    case 'edit_quotation':
      return <ScrollText size={22} />;
    case 'create_invoice':
    case 'edit_invoice':
      return <Receipt size={22} />;
    case 'create_order':
    case 'edit_order':
      return <ClipboardList size={22} />;
    case 'create_task':
    case 'task_due_date':
    case 'task_completed':
      return <Calendar size={22} />;
    case 'edit_task':
      return <Edit size={22} />;
    case 'create_activity':
      return <FileText size={22} />;
    case 'incoming_call':
      return <PhoneIncoming size={22} />;
    case 'outgoing_call':
      return <PhoneOutgoing size={22} />;
    case 'missed_call':
      return <PhoneMissed size={22} />;
    case 'view_page':
    case 'view_file':
      return <Eye size={22} />;
    case 'send_email':
      return <Mail size={22} />;
    case 'send_whatsapp':
      return <MessageCircle size={22} />;
    default:
      return <FileText size={22} />;
  }
};

export default TriggerSection;